/**
 * CardFace — лицо выпущенной карты: картинка дизайна из админки, баланс в правом верхнем углу
 * и «•• 8075» внизу слева (как на референсе), валюта — внизу справа. Номера/срока/CVV у карты
 * нет вовсе. Используется на главной (переворот со счёта), на экране карты и при выпуске.
 *
 * Размеры подобраны под реальную ширину карты в мини-аппе: экран даёт 335–352 px (px-5 плюс
 * max-w-[22rem]), при соотношении 1.586 высота выходит 211–222 px. Отсюда баланс 26 px и
 * подпись 9 px — крупно, но не съедает верх карты и не наезжает на логотип слева.
 *
 * Пропсы: card (выпущенная карта), glow — подсветка после выпуска.
 */
import type { IssuedCard } from "@/lib/api";

export function CardFace({
  card,
  glow = false,
  className = "",
}: {
  card: IssuedCard;
  glow?: boolean;
  className?: string;
}) {
  const image = card.design?.image ?? null;
  return (
    <div
      className={`relative overflow-hidden rounded-[26px] border border-white/10 shadow-2xl shadow-black/60 ${
        glow ? "ring-2 ring-brand/60" : "ring-1 ring-inset ring-white/[0.06]"
      } ${className}`}
      style={{ aspectRatio: "1.586" }}
    >
      {image ? (
        <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-brand-600 via-brand-500 to-surface-base" />
      )}

      {/* скрим снизу — чтобы цифры и валюта читались на любом дизайне */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/70 to-transparent" />
      {/* и сверху — под баланс */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-2/5 bg-gradient-to-b from-black/55 to-transparent" />

      <div className="absolute inset-0 flex flex-col justify-between p-5 text-white [text-shadow:0_1px_6px_rgba(0,0,0,0.6)]">
        {/* баланс — в правом верхнем углу: левый верх остаётся под логотип дизайна */}
        <div className="text-right">
          <div className="text-[9px] font-medium uppercase tracking-[0.16em] text-white/70">Баланс карты</div>
          <div className="mt-1 text-[26px] font-semibold leading-none tracking-tight tabular-nums">
            ${card.balance_usd.toFixed(2)}
          </div>
        </div>

        <div className="flex items-end justify-between">
          {/* две точки + 4 цифры — единственный «номер», который видит клиент */}
          <div className="flex items-center gap-2 text-[17px] font-medium tabular-nums">
            <span className="tracking-[0.2em]">••</span>
            <span>{card.last4}</span>
          </div>
          <div className="text-[17px] font-bold tracking-wide text-white/90">{card.currency}</div>
        </div>
      </div>
    </div>
  );
}
